package com.gloopgo.game

import androidx.compose.animation.ContentTransform
import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.lifecycle.viewmodel.navigation3.rememberViewModelStoreNavEntryDecorator
import androidx.navigation3.runtime.NavKey
import androidx.navigation3.runtime.entryProvider
import androidx.navigation3.runtime.rememberNavBackStack
import androidx.navigation3.runtime.rememberSaveableStateHolderNavEntryDecorator
import androidx.navigation3.ui.NavDisplay
import androidx.navigation3.ui.defaultPredictivePopTransitionSpec
import com.gloopgo.game.di.appModule
import com.gloopgo.game.network.MultiplayerRepository
import com.gloopgo.game.navigation.AvatarColorRoute
import com.gloopgo.game.navigation.ConnectingRoute
import com.gloopgo.game.navigation.HubRoute
import com.gloopgo.game.navigation.InGameRoute
import com.gloopgo.game.navigation.LeaderboardRoute
import com.gloopgo.game.navigation.MainMenuRoute
import com.gloopgo.game.navigation.MatchSystemBackPolicy
import com.gloopgo.game.navigation.SettingsRoute
import com.gloopgo.game.navigation.shouldBlockPlatformBack
import com.gloopgo.game.navigation.SinglePlayerMenuRoute
import com.gloopgo.game.navigation.SplashRoute
import com.gloopgo.game.navigation.navConfig
import com.gloopgo.game.ui.AvatarColorScreen
import com.gloopgo.game.ui.ConnectingScreen
import com.gloopgo.game.ui.GameScreen
import com.gloopgo.game.ui.HubScreen
import com.gloopgo.game.ui.LeaderboardScreen
import com.gloopgo.game.ui.MainMenuScreen
import com.gloopgo.game.ui.SettingsScreen
import com.gloopgo.game.ui.SplashScreen
import com.gloopgo.game.ui.theme.GloopGoTheme
import com.gloopgo.game.viewmodel.ConnectingViewModel
import com.gloopgo.game.viewmodel.GameViewModel
import com.gloopgo.game.viewmodel.LeaderboardViewModel
import com.gloopgo.game.viewmodel.MenuViewModel
import org.koin.compose.KoinApplication
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import org.koin.dsl.koinConfiguration

@Composable
fun App(
    bottomAdContent: @Composable (() -> Unit)? = null
) {
    KoinApplication(
        configuration = koinConfiguration {
            modules(appModule)
        }
    ) {
        GloopGoTheme {
            AppContent(bottomAdContent = bottomAdContent)
        }
    }
}

@Composable
private fun AppContent(
    bottomAdContent: @Composable (() -> Unit)? = null,
    // Only auth + menu are needed for splash → hub; defer other VMs until their routes
    // so iOS cold start can paint Splash ASAP (LeaderboardVM init starts Firestore observe).
    authViewModel: com.gloopgo.game.viewmodel.AuthViewModel = koinViewModel(),
    menuViewModel: MenuViewModel = koinViewModel()
) {
    var useJoystick by remember { mutableStateOf(false) }
    val backStack = rememberNavBackStack(navConfig, SplashRoute)

    MatchSystemBackPolicy(
        blockSystemBack = shouldBlockPlatformBack(backStack.lastOrNull()),
    ) {
    NavDisplay(
        backStack = backStack,
        // Safety net if NavDisplay still owns the gesture: do not pop a match,
        // and do not slide the previous screen in while the finger is down.
        onBack = {
            if (!shouldBlockPlatformBack(backStack.lastOrNull())) {
                backStack.removeLastOrNull()
            }
        },
        predictivePopTransitionSpec = { edge ->
            if (shouldBlockPlatformBack(backStack.lastOrNull())) {
                ContentTransform(
                    targetContentEnter = EnterTransition.None,
                    initialContentExit = ExitTransition.None,
                )
            } else {
                defaultPredictivePopTransitionSpec<NavKey>().invoke(this, edge)
            }
        },
        entryDecorators = listOf(
            rememberSaveableStateHolderNavEntryDecorator(),
            rememberViewModelStoreNavEntryDecorator()
        ),
        entryProvider = entryProvider {
            entry<SplashRoute> {
                SplashScreen(
                    onSessionChecked = {
                        backStack.clear()
                        backStack.add(HubRoute)
                        menuViewModel.loadUserProfile()
                    },
                    authViewModel = authViewModel
                )
            }
            entry<HubRoute> {
                HubScreen(
                    viewModel = menuViewModel,
                    onSinglePlayer = { backStack.add(SinglePlayerMenuRoute) },
                    onMultiplayer = { backStack.add(MainMenuRoute) },
                    onSettings = { backStack.add(SettingsRoute(useJoystick = useJoystick)) }
                )
            }
            entry<SinglePlayerMenuRoute> {
                MainMenuScreen(
                    isSinglePlayerMode = true,
                    onOpenAvatarColor = { startAtAvatar ->
                        backStack.add(
                            AvatarColorRoute(
                                isSinglePlayer = true,
                                startAtAvatar = startAtAvatar
                            )
                        )
                    },
                    onOpenLeaderboard = { /* SP has no global board */ },
                    onBackToHub = {
                        if (backStack.isNotEmpty() && backStack.last() is SinglePlayerMenuRoute) {
                            backStack.removeLastOrNull()
                        }
                    },
                    viewModel = menuViewModel
                )
            }
            entry<MainMenuRoute> {
                MainMenuScreen(
                    isSinglePlayerMode = false,
                    onOpenAvatarColor = { startAtAvatar ->
                        backStack.add(
                            AvatarColorRoute(
                                isSinglePlayer = false,
                                startAtAvatar = startAtAvatar
                            )
                        )
                    },
                    onOpenLeaderboard = { backStack.add(LeaderboardRoute) },
                    onBackToHub = {
                        if (backStack.isNotEmpty() && backStack.last() is MainMenuRoute) {
                            backStack.removeLastOrNull()
                        }
                    },
                    viewModel = menuViewModel
                )
            }
            entry<AvatarColorRoute> { route ->
                AvatarColorScreen(
                    isSinglePlayer = route.isSinglePlayer,
                    startAtAvatar = route.startAtAvatar,
                    onStartGame = { chosenName, chosenEmoji, chosenColor ->
                        if (backStack.isNotEmpty() && backStack.last() is AvatarColorRoute) {
                            backStack.removeLastOrNull()
                        }
                        if (route.isSinglePlayer) {
                            backStack.add(
                                InGameRoute(
                                    playerName = chosenName,
                                    useJoystick = useJoystick,
                                    playerEmoji = chosenEmoji,
                                    playerColor = chosenColor,
                                    isSinglePlayer = true
                                )
                            )
                        } else {
                            backStack.add(
                                ConnectingRoute(
                                    playerName = chosenName,
                                    playerEmoji = chosenEmoji,
                                    playerColor = chosenColor
                                )
                            )
                        }
                    },
                    onBack = {
                        if (backStack.isNotEmpty() && backStack.last() is AvatarColorRoute) {
                            backStack.removeLastOrNull()
                        }
                    },
                    viewModel = menuViewModel
                )
            }
            entry<ConnectingRoute> { route ->
                val connectingViewModel: ConnectingViewModel = koinViewModel()
                ConnectingScreen(
                    playerName = route.playerName,
                    onJoined = { authedUid ->
                        backStack.removeLastOrNull()
                        backStack.add(
                            InGameRoute(
                                playerName = route.playerName,
                                useJoystick = useJoystick,
                                playerEmoji = route.playerEmoji,
                                playerColor = route.playerColor,
                                isSinglePlayer = false,
                                playerId = authedUid.takeIf { it.isNotEmpty() }
                            )
                        )
                    },
                    onBackToMenu = {
                        backStack.removeLastOrNull()
                    },
                    viewModel = connectingViewModel
                )
            }
            entry<InGameRoute> { route ->
                val gameViewModel: GameViewModel = koinViewModel()
                GameScreen(
                    playerName = route.playerName,
                    useJoystick = route.useJoystick,
                    playerEmoji = route.playerEmoji,
                    playerColor = route.playerColor,
                    isSinglePlayer = route.isSinglePlayer,
                    providedPlayerId = route.playerId,
                    onExitToMenu = {
                        gameViewModel.onExit()
                        if (route.isSinglePlayer) {
                            while (backStack.isNotEmpty() && backStack.last() !is SinglePlayerMenuRoute) {
                                if (backStack.last() is HubRoute) break
                                backStack.removeLastOrNull()
                            }
                            if (backStack.none { it is SinglePlayerMenuRoute }) {
                                if (backStack.none { it is HubRoute }) {
                                    backStack.add(HubRoute)
                                }
                                backStack.add(SinglePlayerMenuRoute)
                            }
                            menuViewModel.loadUserProfile()
                        } else {
                            backStack.clear()
                            backStack.add(HubRoute)
                            backStack.add(MainMenuRoute)
                            menuViewModel.loadUserProfile()
                        }
                    },
                    onToggleJoystick = { useJoystick = it },
                    bottomAdContent = bottomAdContent,
                    viewModel = gameViewModel
                )
            }
            entry<LeaderboardRoute> {
                val leaderboardViewModel: LeaderboardViewModel = koinViewModel()
                LeaderboardScreen(
                    onBackToMenu = { backStack.removeLastOrNull() },
                    viewModel = leaderboardViewModel
                )
            }
            entry<SettingsRoute> {
                val repository: MultiplayerRepository = koinInject()
                SettingsScreen(
                    useJoystick = useJoystick,
                    onToggleJoystick = { useJoystick = it },
                    repository = repository,
                    onUserDataCleared = {
                        menuViewModel.resetUserData()
                        authViewModel.clearLocalSession()
                        useJoystick = false
                        while (backStack.isNotEmpty()) {
                            backStack.removeLastOrNull()
                        }
                        backStack.add(SplashRoute)
                    },
                    onBackToMenu = {
                        if (!backStack.isEmpty()) {
                            backStack.removeLastOrNull()
                        }
                    }
                )
            }
        }
    )
    }
}
